import ITask from './ITask';
import Game from '../../Game';
import { EventEmitter } from 'events';
import { Deferred } from '../Deferred';

export enum TaskEvents {
    START = 'task_queue_start',
    COMPLETE = 'task_queue_complete',
}

export function* delay(duration: number): IterableIterator<number> {
    let elapsed = 0;

    // @ts-ignore
    while (elapsed < duration) elapsed += yield;
}

export default class TaskQueue<T> extends EventEmitter {
    private tasks: Array<{
        task: ITask<T>;
        deferred: Deferred;
    }>;
    private _running: boolean = false;
    private skip: boolean;
    private queueIterator: IterableIterator<any>;
    private queueResult: IteratorResult<any>;
    private currentTask: ITask<T>;

    constructor() {
        super();
        this.tasks = [];
    }

    public update() {
        if (this.queueResult && !this.queueResult.done) {
            this.queueResult = this.queueIterator.next(
                // @ts-ignore
                Game.ticker.getDeltaTime()
            );
        }
    }

    private *run(target: T): IterableIterator<number> {
        this.setRunningTo(true);

        while (this.tasks.length > 0) {
            const first = this.tasks.shift();
            this.currentTask = first.task;

            const taskIterator = this.currentTask.execute(target);

            let taskResult = taskIterator.next(yield);

            while (!this.skip) {
                if (!taskResult || taskResult.done) {
                    // Intentionally not resolving promise on skip
                    first.deferred.control.resolve();
                    break;
                }
                taskResult = taskIterator.next(yield);
                if (!taskResult || taskResult.done) {
                    // Intentionally not resolving promise on skip
                    first.deferred.control.resolve();
                    break;
                }
            }

            this.skip = false;
        }

        this.currentTask = null;
        this.queueResult = null;
        this.queueIterator = null;
        this.setRunningTo(false);
    }

    public getCurrentTask(): ITask<T> {
        return this.currentTask;
    }

    public getTaskCount(): number {
        return this.tasks.length;
    }

    public some(predicate: (task: ITask<T>, index: number) => boolean): boolean {
        return this.tasks.some((t, i) => predicate(t.task, i));
    }

    public forEach(cb: (task: ITask<T>, i: number) => void): boolean {
        return this.tasks.some((t, i) => cb(t.task, i));
    }

    public queue(task: ITask<T>) {
        const deferred = new Deferred();
        this.tasks.push({
            task,
            deferred,
        });
        return deferred.read;
    }

    public queueNext(task: ITask<T>) {
        const deferred = new Deferred();
        this.tasks.unshift({
            task,
            deferred,
        });
        return deferred.read;
    }

    public start(target: T) {
        if (this.isRunning()) return;
        if (this.tasks.length === 0) return;
        this.queueIterator = this.run(target);
        this.queueResult = this.queueIterator.next();
    }

    public clear() {
        // Intentionally not resolving promises
        this.tasks.length = 0;
        this.skip = true;
    }

    public isRunning(): boolean {
        return this._running;
    }

    private setRunningTo(value: boolean) {
        if (value === this._running) return;

        this.emit(value ? TaskEvents.START : TaskEvents.COMPLETE);

        this._running = value;
    }
}
